/**
 * CMS后台 - 视频与动画模块（完整制作功能）
 */

import { useState, useEffect, useRef } from 'react'
import { VITE_API_BASE } from '../config/apiConfig'

const API = VITE_API_BASE
// 复用现有 ADMIN_API_KEY 走 X-Admin-Key 内部 admin 通道（与 AIMarketCustomers.jsx 同模式）
const ADMIN_KEY = 'aim-admin-84deadf05b47284331cbf654'
const authHeaders = () => ({ 'X-Admin-Key': ADMIN_KEY })

// 版本权限等级
const PLAN_LEVEL = { basic: 1, standard: 2, professional: 3, enterprise: 4 }

// 获取机构版本
const getInstitutionPlan = () => {
  const plan = localStorage.getItem('cms_plan') || 'basic'
  return plan
}
const getPlanLevel = () => {
  const plan = getInstitutionPlan()
  return PLAN_LEVEL[plan] || 1
}

// 版本对应的可见产品
const GROWTH_VIDEO_PRODUCTS_BY_PLAN = {
  // 所有版本可见
  all: [
    { id: '735530', name: '活动回顾视频', desc: '照片+活动+姓名', price: 0, tag: '标配·所有版本' },
  ],
  // 标准版及以上可见
  standard: [
    { id: '432637', name: '成就展示视频', desc: '证书+里程碑', price: 99, tag: '主打产品' },
    { id: '949130', name: '年度成长总结', desc: '全年数据汇总', price: 199, tag: '' },
    { id: '25', name: '课程结业视频', desc: '课时+作品+学习内容', price: 199, tag: '' },
    { id: '523847', name: '生日祝福视频', desc: '照片+日期+祝福', price: 39, tag: '' },
  ]
}

const GROWTH_VIDEO_PRODUCTS = [
  { id: '735530', name: '活动回顾视频', desc: '照片+活动+姓名', price: 0, tag: '机构SaaS包含' },
  { id: '432637', name: '成就展示视频', desc: '证书+里程碑', price: 99, tag: '主打产品' },
  { id: '949130', name: '年度成长总结', desc: '全年数据汇总', price: 199, tag: '' },
  { id: '25', name: '课程结业视频', desc: '课时+作品+学习内容', price: 199, tag: '' },
  { id: '523847', name: '生日祝福视频', desc: '照片+日期+祝福', price: 39, tag: '' },
]

export default function VideoAnimationModule() {
  const [activeTab, setActiveTab] = useState('growth-create')
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [message, setMessage] = useState(null)
  const [paintingTasks, setPaintingTasks] = useState([])
  const [growthTasks, setGrowthTasks] = useState([])
  const [children, setChildren] = useState([])
  const [materials, setMaterials] = useState([])
  const [batchMode, setBatchMode] = useState(false)
  const [selectedChildren, setSelectedChildren] = useState([])
  const [userPlan, setUserPlan] = useState('basic')

  useEffect(() => {
    const plan = getInstitutionPlan()
    setUserPlan(plan)
  }, [])

  // 根据版本获取可见产品
  const getVisibleProducts = () => {
    const planLevel = getPlanLevel()
    let products = [...GROWTH_VIDEO_PRODUCTS_BY_PLAN.all]
    if (planLevel >= 2) { // 标准版及以上
      products = [...products, ...GROWTH_VIDEO_PRODUCTS_BY_PLAN.standard]
    }
    return products
  }

  const [growthForm, setGrowthForm] = useState({
    childId: '', productId: '432637', title: '', subtitle: '',
    selectedMaterials: [], photoDuration: 3, transition: 'fade', music: 'warm',
  })
  const [paintingForm, setPaintingForm] = useState({
    childId: '', paintingUrl: '', title: '', style: 'cartoon', duration: 5, music: 'gentle',
  })
  const fileInputRef = useRef(null)
  const photoInputRef = useRef(null)

  useEffect(() => { loadAllData() }, [])

  const loadAllData = async () => {
    setLoading(true)
    try {
      await Promise.all([loadPaintingTasks(), loadGrowthTasks(), loadChildren()])
    } catch (err) { console.error(err) }
    finally { setLoading(false) }
  }

  const loadPaintingTasks = async () => {
    try {
      const res = await fetch(`${API}/painting-video/tasks`)
      const json = await res.json()
      if (json.success) setPaintingTasks(json.data?.list || [])
    } catch (err) { console.error(err) }
  }

  const loadGrowthTasks = async () => {
    try {
      const res = await fetch(`${API}/growth-video/tasks`)
      const json = await res.json()
      if (json.success) setGrowthTasks(json.data?.list || [])
    } catch (err) { console.error(err) }
  }

  // 定时刷新任务状态
  useEffect(() => {
    const interval = setInterval(() => {
      if (growthTasks.some(t => t.status === 'processing') || paintingTasks.some(t => t.status === 'processing')) {
        loadGrowthTasks()
        loadPaintingTasks()
      }
    }, 30000) // 每30秒检查一次
    return () => clearInterval(interval)
  }, [growthTasks, paintingTasks])

  const loadChildren = async () => {
    try {
      const res = await fetch(`${API}/children`)
      const json = await res.json()
      if (json.success) setChildren(json.data || [])
    } catch (err) { console.error(err) }
  }

  const handleSelectChild = async (childId) => {
    setGrowthForm({ ...growthForm, childId, selectedMaterials: [] })
    try {
      // 使用统一API获取孩子所有照片
      const res = await fetch(`${API}/children/${childId}/assets?type=photo`, { headers: authHeaders() })
      const json = await res.json()
      if (json.success) {
        const photos = json.data?.assets?.photo || json.data?.photos || []
        const legacyPhotos = json.data?.legacy_photos || []
        const allPhotos = [...photos, ...legacyPhotos].filter((p, i, arr) => arr.findIndex(x => x.id === p.id) === i)
        setMaterials(allPhotos)
      }
    } catch (err) { console.error(err) }
  }

  const handleUploadPhoto = async (files) => {
    if (!growthForm.childId) { setMessage({ type: 'error', text: '请先选择孩子' }); return }
    if (!files || files.length === 0) return
    setSubmitting(true)
    let successCount = 0
    try {
      for (const file of files) {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('child_id', growthForm.childId)
        formData.append('type', 'photo')
        const res = await fetch(`${API}/materials/upload`, { method: 'POST', body: formData })
        const json = await res.json()
        if (json.success) {
          successCount++
          // 后端materials.js已自动写入child_assets，前端只需更新本地状态
          const photoUrl = json.data?.url || json.data?.thumbnail
          setMaterials(prev => [{
            id: json.data?.id || Date.now().toString() + successCount,
            url: photoUrl,
            thumbnail: photoUrl,
            filename: file.name,
            source: 'cms_upload'
          }, ...prev])
        }
      }
      setMessage({ type: 'success', text: '上传成功 ' + successCount + ' 张' })
    } catch (err) { setMessage({ type: 'error', text: '上传失败' }) }
    finally { setSubmitting(false) }
  }

  const handleCreateGrowthVideo = async () => {
    if (!growthForm.childId) { setMessage({ type: 'error', text: '请选择孩子' }); return }
    if (growthForm.selectedMaterials.length === 0) { setMessage({ type: 'error', text: '请选择照片素材' }); return }
    setSubmitting(true)
    try {
      const product = GROWTH_VIDEO_PRODUCTS.find(p => p.id === growthForm.productId)
      const res = await fetch(`${API}/growth-video/tasks`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          child_id: growthForm.childId,
          title: growthForm.title || product?.name,
          subtitle: growthForm.subtitle,
          material_ids: growthForm.selectedMaterials,
          photo_duration: growthForm.photoDuration,
          transition: growthForm.transition,
          music: growthForm.music,
        })
      })
      const json = await res.json()
      if (json.success) {
        setMessage({ type: 'success', text: `成长记录创建成功！任务号：${json.data.task_id}` })
        setGrowthForm({ childId: '', productId: '432637', title: '', subtitle: '', selectedMaterials: [], photoDuration: 3, transition: 'fade', music: 'warm' })
        setMaterials([])
        loadGrowthTasks()
        setActiveTab('growth-list')
      } else { setMessage({ type: 'error', text: json.error || '创建失败' }) }
    } catch (err) { setMessage({ type: 'error', text: '创建失败' }) }
    finally { setSubmitting(false) }
  }

  const handleGenerateClaimCode = async (taskId, type) => {
    const endpoint = type === 'growth' ? 'growth-video' : 'painting-video'
    try {
      const res = await fetch(`${API}/${endpoint}/${taskId}/claim-code`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
      const json = await res.json()
      if (json.success) {
        const code = json.data.claim_code
        navigator.clipboard.writeText(code).then(() => {
          setMessage({ type: 'success', text: `领取码已复制：${code}` })
        }).catch(() => {
          setMessage({ type: 'success', text: `领取码：${code}（请手动复制）` })
        })
        setTimeout(() => setMessage(null), 10000)
      } else { setMessage({ type: 'error', text: json.error }) }
    } catch (err) { setMessage({ type: 'error', text: '生成失败' }) }
  }

  const handleUploadPainting = async (file) => {
    if (!file) return
    setSubmitting(true)
    const formData = new FormData()
    formData.append('file', file)
    try {
      const res = await fetch(`${API}/upload`, { method: 'POST', body: formData })
      const json = await res.json()
      if (json.success) { setPaintingForm({ ...paintingForm, paintingUrl: json.url }); setMessage({ type: 'success', text: '画作上传成功！' }) }
      else { setMessage({ type: 'error', text: '上传失败' }) }
    } catch (err) { setMessage({ type: 'error', text: '上传失败' }) }
    finally { setSubmitting(false) }
  }

  const handleCreatePaintingVideo = async () => {
    if (!paintingForm.childId) { setMessage({ type: 'error', text: '请选择孩子' }); return }
    if (!paintingForm.paintingUrl) { setMessage({ type: 'error', text: '请上传画作' }); return }
    setSubmitting(true)
    try {
      const res = await fetch(`${API}/painting-video/tasks`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ child_id: paintingForm.childId, title: paintingForm.title, painting_url: paintingForm.paintingUrl, style: paintingForm.style, duration: paintingForm.duration, music: paintingForm.music })
      })
      const json = await res.json()
      if (json.success) {
        setMessage({ type: 'success', text: '画作动画创建成功！' })
        setPaintingForm({ childId: '', paintingUrl: '', title: '', style: 'cartoon', duration: 5, music: 'gentle' })
        loadPaintingTasks()
        setActiveTab('painting-list')
      } else { setMessage({ type: 'error', text: json.error }) }
    } catch (err) { setMessage({ type: 'error', text: '创建失败' }) }
    finally { setSubmitting(false) }
  }

  const getStatusBadge = (status) => {
    const map = { pending: { bg: '#fffbe6', color: '#faad14', text: '等待中' }, processing: { bg: '#e6f4ff', color: '#1890ff', text: '制作中' }, completed: { bg: '#f6ffed', color: '#52c41a', text: '已完成' }, failed: { bg: '#fff1f0', color: '#ff4d4f', text: '失败' } }
    const s = map[status] || { bg: '#f5f5f5', color: '#999', text: status }
    return <span style={{ padding: '2px 10px', borderRadius: 10, fontSize: 12, background: s.bg, color: s.color }}>{s.text}</span>
  }

  
  const toggleChildSelection = (childId) => {
    if (selectedChildren.includes(childId)) setSelectedChildren(selectedChildren.filter(id => id !== childId))
    else setSelectedChildren([...selectedChildren, childId])
  }

  const handleBatchCreate = async () => {
    if (selectedChildren.length === 0) { setMessage({ type: 'error', text: '请选择孩子' }); return }
    setSubmitting(true)
    let successCount = 0
    for (const childId of selectedChildren) {
      const child = children.find(c => c.id === childId)
      try {
        const res = await fetch(API + '/growth-video/tasks', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ child_id: childId, title: (child?.name || '孩子') + '的成长记录', subtitle: '活动回顾', material_ids: [], photo_duration: growthForm.photoDuration, transition: growthForm.transition, music: growthForm.music, template: 'activity_review' })
        })
        const json = await res.json()
        if (json.success) successCount++
      } catch (err) { console.error(err) }
    }
    setMessage({ type: 'success', text: '批量生成完成：成功 ' + successCount + ' 个' })
    setSelectedChildren([])
    setBatchMode(false)
    loadGrowthTasks()
    setActiveTab('growth-list')
    setSubmitting(false)
  }

  if (loading) return <div style={{ padding: 60, textAlign: 'center' }}>加载中...</div>

  return (
    <div style={{ padding: 24 }}>
      {message && <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 1000, maxWidth: 400, padding: '16px 24px', borderRadius: 8, background: message.type === 'success' ? '#52c41a' : '#ff4d4f', color: '#fff', fontSize: 14, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>{message.text}</div>}

      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>视频与动画制作中心</h1>
        <p style={{ fontSize: 13, color: '#999', margin: '8px 0 0' }}>成长记录 + 画作动画</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginBottom: 24 }}>
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: 13, color: '#999', marginBottom: 8 }}>成长记录</div>
          <div style={{ fontSize: 28, fontWeight: 'bold', color: '#722ed1' }}>{growthTasks.length}</div>
        </div>
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: 13, color: '#999', marginBottom: 8 }}>制作中</div>
          <div style={{ fontSize: 28, fontWeight: 'bold', color: '#1890ff' }}>{growthTasks.filter(t => t.status === 'processing').length + paintingTasks.filter(t => t.status === 'processing').length}</div>
        </div>
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: 13, color: '#999', marginBottom: 8 }}>已完成</div>
          <div style={{ fontSize: 28, fontWeight: 'bold', color: '#52c41a' }}>{growthTasks.filter(t => t.status === 'completed').length + paintingTasks.filter(t => t.status === 'completed').length}</div>
        </div>
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: 13, color: '#999', marginBottom: 8 }}>画作动画</div>
          <div style={{ fontSize: 28, fontWeight: 'bold', color: '#eb2f96' }}>{paintingTasks.length}</div>
        </div>
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: 13, color: '#999', marginBottom: 8 }}>失败</div>
          <div style={{ fontSize: 28, fontWeight: 'bold', color: '#ff4d4f' }}>{growthTasks.filter(t => t.status === 'failed').length + paintingTasks.filter(t => t.status === 'failed').length}</div>
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <div style={{ display: 'flex', borderBottom: '1px solid #f0f0f0' }}>
          {[{ key: 'growth-create', label: '创建成长记录' }, { key: 'growth-list', label: '成长记录列表' }, { key: 'painting-create', label: '创建画作动画' }, { key: 'painting-list', label: '画作动画列表' }].map(tab => (
            <button key={tab.key} onClick={() => { setActiveTab(tab.key); setMessage(null); setBatchMode(false) }} style={{ padding: '16px 24px', border: 'none', background: activeTab === tab.key ? '#722ed1' : 'transparent', color: activeTab === tab.key ? '#fff' : '#666', cursor: 'pointer', fontSize: 14 }}>
              {tab.label}
            </button>
          ))}
        </div>

        <div style={{ padding: 24 }}>
          {activeTab === 'growth-create' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}><h3 style={{ margin: 0 }}>创建成长记录</h3><button onClick={() => setBatchMode(true)} style={{ padding: '8px 16px', background: '#52c41a', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>批量生成（活动回顾）</button></div>
              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', marginBottom: 12, fontWeight: 500 }}>选择产品</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                  {getVisibleProducts().map(p => (
                    <div key={p.id} onClick={() => setGrowthForm({ ...growthForm, productId: p.id })} style={{ padding: 16, borderRadius: 10, border: growthForm.productId === p.id ? '2px solid #722ed1' : '1px solid #e8e8e8', background: growthForm.productId === p.id ? '#722ed110' : '#fff', cursor: 'pointer' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontWeight: 600 }}>{p.name}</span>
                        {p.tag && <span style={{ fontSize: 10, padding: '2px 6px', background: '#722ed1', color: '#fff', borderRadius: 4 }}>{p.tag}</span>}
                      </div>
                      <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>{p.desc}</div>
                      <div style={{ fontSize: 18, fontWeight: 'bold', color: p.price === 0 ? '#52c41a' : '#fa8c16' }}>{p.price === 0 ? '免费' : `¥${p.price}`}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
                <div>
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>选择孩子 *</label>
                    <select value={growthForm.childId} onChange={(e) => handleSelectChild(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: 8, border: '1px solid #d9d9d9', fontSize: 14 }}>
                      <option value="">请选择孩子</option>
                      {children.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>视频标题</label>
                    <input type="text" value={growthForm.title} onChange={(e) => setGrowthForm({ ...growthForm, title: e.target.value })} placeholder="如：张小明的成长记录" style={{ width: '100%', padding: '12px', borderRadius: 8, border: '1px solid #d9d9d9', fontSize: 14 }} />
                  </div>
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>照片切换时长</label>
                    <div style={{ display: 'flex', gap: 12 }}>
                      {[2, 3, 4, 5].map(d => <button key={d} onClick={() => setGrowthForm({ ...growthForm, photoDuration: d })} style={{ flex: 1, padding: '10px', border: growthForm.photoDuration === d ? '2px solid #722ed1' : '1px solid #e8e8e8', borderRadius: 6, background: growthForm.photoDuration === d ? '#722ed110' : '#fff', cursor: 'pointer' }}>{d}秒</button>)}
                    </div>
                  </div>
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>背景音乐</label>
                    <select value={growthForm.music} onChange={(e) => setGrowthForm({ ...growthForm, music: e.target.value })} style={{ width: '100%', padding: '12px', borderRadius: 8, border: '1px solid #d9d9d9', fontSize: 14 }}>
                      <option value="warm">温暖回忆</option>
                      <option value="happy">欢快活泼</option>
                      <option value="gentle">柔和轻缓</option>
                      <option value="none">无音乐</option>
                    </select>
                  </div>
                  <button onClick={handleCreateGrowthVideo} disabled={submitting || !growthForm.childId || growthForm.selectedMaterials.length === 0} style={{ width: '100%', padding: '16px', background: (submitting || !growthForm.childId || growthForm.selectedMaterials.length === 0) ? '#ccc' : '#722ed1', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 16, fontWeight: 600 }}>
                    {submitting ? '创建中...' : '开始生成成长记录'}
                  </button>
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <label style={{ fontWeight: 500 }}>选择照片素材（{growthForm.selectedMaterials.length}张）</label>
                    {growthForm.childId && <button onClick={() => photoInputRef.current?.click()} style={{ padding: '6px 12px', background: '#722ed1', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>上传照片</button>}
                  </div>
                  <input ref={photoInputRef} type="file" accept="image/*" multiple onChange={(e) => handleUploadPhoto(e.target.files)} style={{ display: 'none' }} />

                  {!growthForm.childId ? (
                    <div style={{ padding: 40, textAlign: 'center', color: '#999', border: '2px dashed #d9d9d9', borderRadius: 12 }}>请先选择孩子</div>
                  ) : materials.length === 0 ? (
                    <div onClick={() => photoInputRef.current?.click()} style={{ padding: 60, textAlign: 'center', cursor: 'pointer', border: '2px dashed #722ed1', borderRadius: 12, background: '#fafafa' }}>
                      <div style={{ fontSize: 48, marginBottom: 16 }}>点击上传照片</div>
                      <div style={{ color: '#722ed1', fontSize: 14 }}>支持 JPG、PNG 格式</div>
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, maxHeight: 400, overflowY: 'auto' }}>
                      {materials.map(m => (
                        <div key={m.id} onClick={() => { const sel = growthForm.selectedMaterials.includes(m.id) ? growthForm.selectedMaterials.filter(id => id !== m.id) : [...growthForm.selectedMaterials, m.id]; setGrowthForm({ ...growthForm, selectedMaterials: sel }) }} style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', cursor: 'pointer', border: growthForm.selectedMaterials.includes(m.id) ? '3px solid #722ed1' : '1px solid #e8e8e8' }}>
                          <img src={m.thumbnail || m.url} alt="" style={{ width: '100%', aspectRatio: '1', objectFit: 'cover' }} />
                          {growthForm.selectedMaterials.includes(m.id) && <div style={{ position: 'absolute', top: 4, right: 4, width: 20, height: 20, borderRadius: '50%', background: '#722ed1', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>✓</div>}
                        </div>
                      ))}
                    </div>
                  )}
                  {materials.length > 0 && <button onClick={() => photoInputRef.current?.click()} style={{ width: '100%', padding: '10px', marginTop: 12, background: '#f5f5f5', border: '1px solid #d9d9d9', borderRadius: 6, cursor: 'pointer' }}>+ 添加更多照片</button>}
                </div>
              </div>
            </div>
          )}

          
          {activeTab === 'growth-create' && batchMode && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h3 style={{ margin: 0, color: '#52c41a' }}>批量生成活动回顾视频（免费）</h3>
                <button onClick={() => setBatchMode(false)} style={{ padding: '8px 16px', background: '#f5f5f5', border: 'none', borderRadius: 6, cursor: 'pointer' }}>返回单条制作</button>
              </div>
              <div style={{ background: '#f6ffed', padding: 16, borderRadius: 8, marginBottom: 24, border: '1px solid #b7eb8f' }}>
                <div style={{ fontWeight: 500, marginBottom: 8, color: '#52c41a' }}>批量生成说明</div>
                <ul style={{ margin: 0, paddingLeft: 20, color: '#666', fontSize: 13 }}>
                  <li>活动回顾视频对所有版本免费，无权限限制</li>
                  <li>选择一个或多个孩子，自动生成视频</li>
                </ul>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 12, fontWeight: 500 }}>选择孩子（可多选）：已选 {selectedChildren.length} 人</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, maxHeight: 300, overflowY: 'auto' }}>
                  {children.map(c => (
                    <div key={c.id} onClick={() => toggleChildSelection(c.id)} style={{ padding: 16, borderRadius: 8, border: selectedChildren.includes(c.id) ? '2px solid #52c41a' : '1px solid #e8e8e8', background: selectedChildren.includes(c.id) ? '#f6ffed' : '#fff', cursor: 'pointer' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 20, height: 20, borderRadius: 4, border: selectedChildren.includes(c.id) ? '2px solid #52c41a' : '1px solid #d9d9d9', background: selectedChildren.includes(c.id) ? '#52c41a' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {selectedChildren.includes(c.id) && <span style={{ color: '#fff', fontSize: 12 }}>✓</span>}
                        </div>
                        <span style={{ fontWeight: 500 }}>{c.name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>照片切换时长</label>
                <div style={{ display: 'flex', gap: 12 }}>
                  {[2, 3, 4, 5].map(d => <button key={d} onClick={() => setGrowthForm({ ...growthForm, photoDuration: d })} style={{ flex: 1, padding: '10px', border: growthForm.photoDuration === d ? '2px solid #52c41a' : '1px solid #e8e8e8', borderRadius: 6, background: growthForm.photoDuration === d ? '#f6ffed' : '#fff', cursor: 'pointer' }}>{d}秒</button>)}
                </div>
              </div>
              <button onClick={handleBatchCreate} disabled={submitting || selectedChildren.length === 0} style={{ width: '100%', padding: '16px', background: (submitting || selectedChildren.length === 0) ? '#ccc' : '#52c41a', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 16, fontWeight: 600 }}>
                {submitting ? '生成中...' : '批量生成 ' + selectedChildren.length + ' 个活动回顾视频'}
              </button>
            </div>
          )}

          {activeTab === 'growth-create' && !batchMode && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
                <h3 style={{ margin: 0 }}>成长记录列表</h3>
                <button onClick={loadGrowthTasks} style={{ padding: '8px 16px', background: '#f5f5f5', border: 'none', borderRadius: 6, cursor: 'pointer' }}>刷新</button>
              </div>
              {growthTasks.length === 0 ? <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>暂无任务</div> : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: 16 }}>
                  {growthTasks.map(task => (
                    <div key={task.id} style={{ background: '#fafafa', borderRadius: 12, padding: 20 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                        <div><div style={{ fontWeight: 600 }}>{task.title || '成长记录'}</div><div style={{ fontSize: 12, color: '#999' }}>{task.child_name}</div></div>
                        {getStatusBadge(task.status)}
                      </div>
                      <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>{task.material_count || 0}张照片</div>
                      <div style={{ fontSize: 11, color: '#999', marginBottom: 12 }}>{new Date(task.created_at).toLocaleString()}</div>
                      {task.status === 'completed' && (
                        <div>
                          {(task.output_url || task.video_url) && <button onClick={() => window.open(task.output_url || task.video_url, '_blank')} style={{ width: '100%', padding: '10px', background: '#1890ff', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', marginBottom: 8 }}>预览视频</button>}
                          <button onClick={() => handleGenerateClaimCode(task.id, 'growth')} style={{ width: '100%', padding: '10px', background: '#52c41a', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>生成领取码</button>
                        </div>
                      )}
                      {task.status === 'processing' && <div style={{ textAlign: 'center', padding: 10, background: '#e6f4ff', borderRadius: 6, color: '#1890ff' }}>AI生成中...</div>}
                      {task.status === 'failed' && <div style={{ padding: 10, background: '#fff1f0', borderRadius: 6, color: '#ff4d4f', fontSize: 12 }}>{task.error_message}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'painting-create' && (
            <div>
              <h3 style={{ marginBottom: 24 }}>创建画作动画</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
                <div>
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>选择孩子 *</label>
                    <select value={paintingForm.childId} onChange={(e) => setPaintingForm({ ...paintingForm, childId: e.target.value })} style={{ width: '100%', padding: '12px', borderRadius: 8, border: '1px solid #d9d9d9', fontSize: 14 }}>
                      <option value="">请选择孩子</option>
                      {children.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>动画风格</label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                      {[{k:'cartoon',l:'卡通'}, {k:'watercolor',l:'水彩'}, {k:'oil',l:'油画'}, {k:'sketch',l:'素描'}].map(s => (
                        <div key={s.k} onClick={() => setPaintingForm({ ...paintingForm, style: s.k })} style={{ padding: 12, borderRadius: 8, border: paintingForm.style === s.k ? '2px solid #722ed1' : '1px solid #e8e8e8', background: paintingForm.style === s.k ? '#722ed110' : '#fff', cursor: 'pointer', textAlign: 'center' }}><div>{s.l}</div></div>
                      ))}
                    </div>
                  </div>
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>视频时长</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {[3,5,10,15].map(d => <button key={d} onClick={() => setPaintingForm({ ...paintingForm, duration: d })} style={{ flex: 1, padding: '10px', border: paintingForm.duration === d ? '2px solid #722ed1' : '1px solid #e8e8e8', borderRadius: 6, background: paintingForm.duration === d ? '#722ed110' : '#fff', cursor: 'pointer' }}>{d}秒</button>)}
                    </div>
                  </div>
                  <button onClick={handleCreatePaintingVideo} disabled={submitting || !paintingForm.childId || !paintingForm.paintingUrl} style={{ width: '100%', padding: '16px', background: (submitting || !paintingForm.childId || !paintingForm.paintingUrl) ? '#ccc' : '#722ed1', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 16 }}>
                    {submitting ? '创建中...' : '开始生成动画'}
                  </button>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>上传画作图片 *</label>
                  <div onClick={() => fileInputRef.current?.click()} style={{ border: '2px dashed #d9d9d9', borderRadius: 12, padding: 40, textAlign: 'center', cursor: 'pointer', background: paintingForm.paintingUrl ? '#f6ffed' : '#fafafa' }}>
                    {paintingForm.paintingUrl ? <><img src={paintingForm.paintingUrl} alt="" style={{ maxWidth: '100%', maxHeight: 250, borderRadius: 8 }} /><div style={{ marginTop: 12, color: '#52c41a' }}>已上传，点击更换</div></> : <><div style={{ fontSize: 48, marginBottom: 16 }}>点击上传画作</div><div style={{ color: '#999', fontSize: 12 }}>支持 JPG、PNG</div></>}
                  </div>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={(e) => handleUploadPainting(e.target.files?.[0])} style={{ display: 'none' }} />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'painting-list' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
                <h3 style={{ margin: 0 }}>画作动画列表</h3>
                <button onClick={loadPaintingTasks} style={{ padding: '8px 16px', background: '#f5f5f5', border: 'none', borderRadius: 6, cursor: 'pointer' }}>刷新</button>
              </div>
              {paintingTasks.length === 0 ? <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>暂无任务</div> : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
                  {paintingTasks.map(task => (
                    <div key={task.id} style={{ background: '#fafafa', borderRadius: 12, padding: 20 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                        <div style={{ fontWeight: 600 }}>{task.title || '画作动画'}</div>
                        {getStatusBadge(task.status)}
                      </div>
                      <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>{task.style} - {task.duration}秒</div>
                      {task.status === 'completed' && (
                        <div>
                          {(task.output_url || task.video_url) && <button onClick={() => window.open(task.output_url || task.video_url, '_blank')} style={{ width: '100%', padding: '10px', background: '#eb2f96', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', marginBottom: 8 }}>预览动画</button>}
                          <button onClick={() => handleGenerateClaimCode(task.id, 'painting')} style={{ width: '100%', padding: '10px', background: '#52c41a', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>生成领取码</button>
                        </div>
                      )}
                      {task.status === 'processing' && <div style={{ textAlign: 'center', padding: 10, background: '#e6f4ff', borderRadius: 6, color: '#1890ff' }}>生成中...</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
